import {useUserStore} from '@/app/shared/state/UserDataModule';
import {UnaMapRequest} from '../../una-routes';
import {EAlertType} from '@/app/shared/state/AlertModule';
import * as KlipApi from '@/api/klip-api.proxy';
import * as UnaModel from '../../shared/una-model';
import {
    castStringToSearchField,
    MapRequestSearchField
} from '@/app/shared/components/kl-search-maprequests/kl-search-maprequests';
import KlSearchMaprequests from '@/app/shared/components/kl-search-maprequests/kl-search-maprequests.vue';
import {escape} from 'html-escaper';
import {computed, defineComponent, onBeforeMount, onMounted, reactive, ref, useTemplateRef, watch} from 'vue';
import {useAlertHandler} from '@/plugins/alert';
import {EUserRole} from '@/app/shared/state/user-role';
import {useKlModalEventsUtil} from '@/app/shared/helpers/kl-modal-events-util';
import DateUtil from '@/app/shared/helpers/date-util';
import {useUnaStore} from '@/app/una/una-store';
import {IFilterData, IMapRequestMeta} from '@/app/una/maprequest-store-context';
import {IpproDatatable} from '@/app/shared/components/ippro-datatable/ippro-datatable-declare';
import {isEmpty} from 'lodash-es';

export default defineComponent({
    name: 'KlUnaToConfirm',
    emits: ['update-query'],
    setup(props, { emit }) {

        const confirmAllModalEvents = useKlModalEventsUtil('confirm-all-modal');
        const searchMaprequests = useTemplateRef<InstanceType<typeof KlSearchMaprequests>>('searchMaprequests');

        const confirmCheckedMapRequests = (input: KlipApi.ConfirmMapRequestsInput[]) => useUnaStore().confirmMapRequests(input);

        const unaStore = useUnaStore();
        const toConfirm = computed(() => unaStore.toConfirm);

        const selectedZone_AllOption = 'all';

        const mapRequests = computed((): UnaModel.IMapRequestListItem[] => toConfirm.value.mapRequests);
        const mapRequestsColumns = computed((): KlipApi.IColumn[] => toConfirm.value.mapRequestsColumns);
        const mapRequestsFetching = computed((): boolean => toConfirm.value.mapRequestsFetching);
        const mapRequestsMeta = computed((): IMapRequestMeta => toConfirm.value.mapRequestsMeta);

        const searchUsed = ref<boolean>(false);
        const checkedMapRequests = ref<UnaModel.IMapRequestListItem[]>([]);
        const confirmingMaprequests = ref<boolean>(false);
        const confirmingAll = ref<boolean>(false);

        const confirmCheckedMaprequestsEnabled = computed(() => {
            return !!(mapRequests.value
                && mapRequests.value.length
                && checkedMapRequests.value.length
                && !confirmingMaprequests.value);
        });

        const isUnaWriter = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.una_operator]);
        });

        const pagerClicked = (page: number) => {
            toConfirm.value.updatePagination(page);
        };

        const columnClicked = (column: KlipApi.IColumn) => {
            toConfirm.value.updateSorting(column);
        };

        function _apiInputToFilter(input: KlipApi.IGetMapRequestsToConfirmInput): MapRequestSearchField {
            if (!input) {
                return null;
            }
            return {
                searchField: input.searchField ? KlipApi.SearchField[input.searchField] : null,
                organisationId: input.organisationId,
                searchOption: input.organisationId || KlipApi.SearchOption.All.toString(),
                keyword: input.query || '',
                date: {
                    from: DateUtil.parseToDate(input.startDate),
                    to: DateUtil.parseToDate(input.endDate),
                },
            };
        }

        function _filterToApi(searchFields: MapRequestSearchField): KlipApi.IGetMapRequestsToConfirmInput {
            console.assert(searchFields, 'Invalid argument: searchFields is required');
            return {
                query: searchFields.keyword,
                organisationId: searchFields.organisationId, //searchFields.searchOption === KlipApi.SearchOption.All.toString() ? null : searchFields.searchOption,
                searchOption: !searchFields.organisationId ? KlipApi.SearchOption.All : KlipApi.SearchOption.SubOrganisation, // castStringToSearchOption(searchFields.searchOption, KlipApi.SearchOption.SubOrganisation) as any,
                searchField: castStringToSearchField(searchFields.searchField),
                startDate: DateUtil.formatForApi(searchFields.date.from, 'ISO'),
                endDate: DateUtil.formatForApi(searchFields.date.to, 'ISO'),
            }
        }

        const mapRequestDefaultFilter = computed((): MapRequestSearchField => {
            return _apiInputToFilter(toConfirm.value.filterData.defaultSearchFilter);
        });

        const maprequestCurrentFilter = computed((): MapRequestSearchField => {
            return _apiInputToFilter(toConfirm.value.mapRequestsInput);
        })

        const selectedZone = computed({
            get (): string {
                return toConfirm.value.mapRequestsInput?.utilityNetworkAuthorityZoneId || selectedZone_AllOption;
            },
            set (newZoneId: string){
                toConfirm.value.updateFilter(_applySelectedZoneToInput(toConfirm.value.mapRequestsInput, newZoneId));
            }
        })

        const filterData = computed((): IFilterData<KlipApi.IGetMapRequestsToConfirmInput> => toConfirm.value.filterData);
        const mapRequestsHasZones = computed(() => !isEmpty(toConfirm.value.filterData.zones));


        const checkboxChanged = (checkedRows: UnaModel.IMapRequestListItem[]) => {
            checkedMapRequests.value = checkedRows;
        }

        const search = (searchFields: MapRequestSearchField) => {
            searchUsed.value = true;

            toConfirm.value.updateFilter(_applyFilterToInput(toConfirm.value.mapRequestsInput, searchFields));
        }

        const _applyFilterToInput = (input: KlipApi.IGetMapRequestsToConfirmInput, searchFields: MapRequestSearchField): KlipApi.IGetMapRequestsToConfirmInput => {
            const newInput = _filterToApi(searchFields);
            return {
                ...input,
                ...newInput,
            };
        }

        const _applySelectedZoneToInput = (input: KlipApi.IGetMapRequestsToConfirmInput, zoneId: string): KlipApi.IGetMapRequestsToConfirmInput => {
            const utilityNetworkAuthorityZoneId = zoneId === selectedZone_AllOption ? null : zoneId;
            return {
                ...input,
                utilityNetworkAuthorityZoneId,
            };
        }

        const clearSearch = () => {
            searchMaprequests.value.reset();
            selectedZone.value = null;
        }

        const _parseCell = (row: IpproDatatable.IRow, column: IpproDatatable.IColumn): any => {
            const cellValue = escape(row[column.key])
            return {
                template: row.mapRequestId && row.unaZoneId ? `<kl-router-link :to='${JSON.stringify(UnaMapRequest)}'
             :params='{id:"${row.mapRequestId}", archived:"false", zoneId:"${row.unaZoneId}"}'>${cellValue}</kl-router-link>` :
                    `<span>${cellValue}</span>`,
            };
        }

        const _toggleModal = () => {
            confirmAllModalEvents.triggerModalToggle();
        }

        const confirmCheckedMaprequests = () => {
            if (toConfirm.value.mapRequestsInput.searchOption === KlipApi.SearchOption.All && mapRequests.value && mapRequests.value.length === checkedMapRequests.value.length) {
                _toggleModal();
                return;
            }
            _confirmMapRequest();
        }

        const confirmAll = async () => {
            _confirmMapRequest();
        }

        // TODO: move to store
        const _confirmMapRequest = async () => {
            confirmingMaprequests.value = true;
            const maprequestsToConfirm: KlipApi.ConfirmMapRequestsInput[] =
                checkedMapRequests.value.map((obj: UnaModel.IMapRequestListItem) => {
                    return KlipApi.ConfirmMapRequestsInput.fromJS({
                        mapRequestId: obj.mapRequestId,
                        zoneId: obj.unaZoneId,
                    });
                });

            const numberOfMaprequestToConfirm = maprequestsToConfirm.length;

            await confirmCheckedMapRequests(maprequestsToConfirm);

            // als alle rows checked waren, goto next page
            // .. geen idee of dit echt werkt .. (updatePagination doet een bff call, en ondertussen is de backend die confirmCheckedMapRequests aan het processen)
            if (numberOfMaprequestToConfirm === mapRequestsMeta.value.resultsPerPage) {
                await toConfirm.value.updatePagination(mapRequestsMeta.value.currentPage + 1);
            }

            confirmingMaprequests.value = false;

            const toasterId = `confirm-maprequest-toaster-${Math.random().toString(36).substring(7)}`;
            useAlertHandler().addAlert({
                type: EAlertType.Success,
                id: toasterId,
                title: 'Ontvangst bevestigd',
                message: `U heeft ${maprequestsToConfirm.length} ${maprequestsToConfirm.length > 1 ?
                    'planaanvragen' : 'planaanvraag'} bevestigd.`,
                icon: 'info-circle',
                autoClose: 5000,
                closable: true,
            });
        }

        watch(
            mapRequests,
            (newMapRequests) => {
                checkedMapRequests.value = newMapRequests ? newMapRequests.filter((c) => c.checked) : [];
            },
            { immediate: true, deep: true });

        onBeforeMount(async () => {
            await useUnaStore().initToConfirm(_parseCell);
        });

        return {
            mapRequests,
            searchUsed,
            mapRequestsMeta,
            mapRequestsColumns,
            mapRequestsFetching,
            confirmingMaprequests,
            selectedZone,
            confirmingAll,
            confirmCheckedMaprequestsEnabled,

            isUnaWriter,
            pagerClicked,
            columnClicked,
            checkboxChanged,
            search,
            confirmCheckedMaprequests,
            confirmAll,

            clearSearch,

            filterData,
            mapRequestsHasZones,
            mapRequestDefaultFilter,
            maprequestCurrentFilter,
        };
    }
})
